import React, { useState, useRef } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  Divider,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { motion, AnimatePresence, useInView } from "framer-motion";
import GroupsIcon from '@mui/icons-material/Groups';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CancelIcon from '@mui/icons-material/Cancel';

// Mode data with enhanced information
const MODES = {
  Team: {
    title: "Team Streaks",
    icon: <GroupsIcon sx={{ fontSize: "2rem" }} />,
    description:
      "Collaborate and maintain streaks together. The team's success depends on every member showing up - if one person fails, the streak resets for everyone! Perfect for building group accountability.",
    benefits: [
      "Build collective accountability",
      "Motivate each other to stay consistent",
      "Track team progress in real-time"
    ],
    color: "#4C9AFF",
    images: ["/campus.png", "/teamstreak.png"],
  },
  Elimination: {
    title: "Elimination",
    icon: <CancelIcon sx={{ fontSize: "2rem" }} />,
    description:
      "A high-stakes challenge where users are eliminated if they break their streak without rest or sick days remaining. Last person standing wins! Perfect for competitive groups.",
    benefits: [
      "Create exciting competitive challenges",
      "Increase motivation with stakes",
      "Celebrate the ultimate winner"
    ],
    color: "#FF5630",
    images: ["/yoga2.png", "/elim.png"],
  },
  Streaks: {
    title: "Streaks",
    icon: <WhatshotIcon sx={{ fontSize: "2rem" }} />,
    description: 
      "Track individual streaks and build personal consistency. Set goals, monitor your progress, and celebrate milestone achievements as you develop powerful habits.",
    benefits: [
      "Build personal consistency",
      "Track your progress over time",
      "Celebrate milestone achievements"
    ],
    color: "#FFC400",
    images: ["/yoga.png", "/streaks.png"],
  },
};

const FeatureModes = () => {
  const [activeMode, setActiveMode] = useState("Team");
  const current = MODES[activeMode];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <Box
      ref={ref}
      id="modes"
      sx={{
        position: "relative",
        py: { xs: 12, md: 16 },
        overflow: "hidden",
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, #1A1A35 0%, #24243F 50%, #1A1A35 100%)`,
          zIndex: -2,
        }}
      />
      
      {/* Animated background elements */}
      <Box
        component={motion.div}
        animate={{
          y: ["-5%", "5%"],
          x: ["3%", "-3%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 15,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: "40vw",
          height: "40vw",
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(155, 90, 242, 0.2) 0%, rgba(155, 90, 242, 0) 70%)`,
          filter: "blur(60px)",
          top: "-10%",
          right: "-5%",
          zIndex: -1,
        }}
      />
      
      <Box
        component={motion.div}
        animate={{
          y: ["5%", "-5%"],
          x: ["-2%", "2%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 20,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: "30vw",
          height: "30vw",
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(76, 154, 255, 0.15) 0%, rgba(76, 154, 255, 0) 70%)`,
          filter: "blur(60px)",
          bottom: "-10%",
          left: "20%",
          zIndex: -1,
        }}
      />

      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="overline"
                sx={{
                  color: "#9b5af2",
                  fontSize: "0.9rem",
                  letterSpacing: 2,
                  mb: 1,
                  display: "block",
                  fontWeight: 600,
                }}
              >
                CHALLENGE YOURSELF
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2.2rem", md: "3.2rem" },
                  background: "linear-gradient(to right, #ffffff, #9b5af2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 2,
                }}
              >
                Three Unique Modes
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1rem", md: "1.2rem" },
                  color: "rgba(255, 255, 255, 0.8)",
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 6,
                }}
              >
                Choose how you want to challenge yourself or compete with friends.
                Each mode offers a different experience to keep you motivated and consistent.
              </Typography>
            </motion.div>
          </Box>

          {/* Mode Selection Tabs */}
          <motion.div variants={itemVariants}>
            <Box 
              sx={{ 
                display: "flex", 
                justifyContent: "center",
                flexWrap: "wrap",
                gap: { xs: 1, md: 2 },
                mb: 6,
              }}
            >
              {Object.keys(MODES).map((mode) => (
                <Button
                  key={mode}
                  variant={activeMode === mode ? "contained" : "outlined"}
                  onClick={() => setActiveMode(mode)}
                  startIcon={MODES[mode].icon}
                  sx={{
                    borderRadius: "12px",
                    px: { xs: 2, md: 3 },
                    py: 1.5,
                    mb: { xs: 1, md: 0 },
                    backgroundColor: activeMode === mode ? "#9b5af2" : "rgba(155, 90, 242, 0.1)",
                    borderColor: "#9b5af2",
                    color: activeMode === mode ? "white" : "#9b5af2",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: activeMode === mode ? "0 8px 25px rgba(155, 90, 242, 0.4)" : "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: activeMode === mode ? "#8347d6" : "rgba(155, 90, 242, 0.2)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 10px 25px rgba(155, 90, 242, 0.4)",
                    },
                  }}
                >
                  {MODES[mode].title}
                </Button>
              ))}
            </Box>
          </motion.div>

          {/* Mode Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeMode}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.5,
                type: "spring",
                stiffness: 150,
                damping: 20
              }}
            >
              <Grid container spacing={4} alignItems="center">
                {/* Left side - Content */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "24px",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      overflow: "hidden",
                      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
                      height: "100%",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    {/* Mode header with colored accent */}
                    <Box
                      sx={{
                        p: 3,
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: `${current.color}20`,
                          color: current.color,
                          borderRadius: "12px",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {current.icon}
                      </Box>
                      
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "white",
                        }}
                      >
                        {current.title}
                      </Typography>
                    </Box>
                    
                    {/* Mode description */}
                    <Box sx={{ p: 3 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          mb: 3,
                          lineHeight: 1.7,
                        }}
                      >
                        {current.description}
                      </Typography>
                      
                      <Divider sx={{ my: 3, backgroundColor: "rgba(255, 255, 255, 0.1)" }} />
                      
                      {/* Benefits list */}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.6)",
                          mb: 2,
                          fontWeight: 500,
                        }}
                      >
                        KEY BENEFITS
                      </Typography>
                      
                      {current.benefits.map((benefit, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: current.color,
                              mr: 2,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "white",
                            }}
                          >
                            {benefit}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>
                
                {/* Right side - Image */}
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      transform: { xs: "scale(1)", md: "scale(1.15)" },
                      ml: { xs: 0, md: -4 },
                    }}
                  >
                    {/* Glow effect */}
                    <Box
                      sx={{
                        position: "absolute",
                        width: "70%",
                        height: "70%",
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${current.color}30 0%, transparent 70%)`,
                        filter: "blur(40px)",
                        zIndex: 0,
                      }}
                    />
                    
                    {/* Phone mockup */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: { xs: "340px", md: "500px" },
                        mx: "auto",
                        zIndex: 1,
                      }}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#121212",
                            borderRadius: "36px",
                            padding: "12px",
                            boxShadow: `0 30px 60px rgba(0, 0, 0, 0.4), 0 0 40px ${current.color}40`,
                            border: "2px solid rgba(70, 70, 100, 0.6)",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: "28px",
                              overflow: "hidden",
                              border: "none",
                            }}
                          >
                            <Box
                              component="img"
                              src={current.images[0]}
                              alt={`${current.title} mode`}
                              sx={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                                border: "none",
                              }}
                            />
                          </Box>
                          
                          {/* Reflection effect */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "30%",
                              background: "linear-gradient(to bottom, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0))",
                              borderTopLeftRadius: "36px",
                              borderTopRightRadius: "36px",
                              pointerEvents: "none",
                            }}
                          />
                        </Box>
                      </motion.div>
                    </Box>
                    
                    {/* Mode label */}

                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FeatureModes;